/**
 * Scenty campaign discount — UTM / promo link handling.
 * Persists discount code in sessionStorage, updates price displays,
 * and redirects via /discount/CODE?redirect=... at checkout.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'sc_campaign_discount';
  var URGENCY_DISMISS_KEY = 'sc_campaign_urgency_dismissed';
  var CONFIG = null;
  var ACTIVE = null;
  var urgencyTimerId = null;

  function parseConfig() {
    if (CONFIG) return CONFIG;
    var node = document.getElementById('sc-campaign-discount-config');
    if (!node) {
      CONFIG = { campaigns: {}, utmMap: {}, labelDefault: 'Din kampagnepris' };
      return CONFIG;
    }
    try {
      CONFIG = JSON.parse(node.textContent || '{}');
    } catch (e) {
      CONFIG = { campaigns: {}, utmMap: {}, labelDefault: 'Din kampagnepris' };
    }
    if (!CONFIG.campaigns) CONFIG.campaigns = {};
    if (!CONFIG.utmMap) CONFIG.utmMap = {};
    if (CONFIG.storageKey) STORAGE_KEY = CONFIG.storageKey;
    return CONFIG;
  }

  function parsePriceAmount(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && !isNaN(value)) return value;
    var match = String(value).replace(/\./g, '').match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  function formatKr(amount) {
    return amount + ' kr.';
  }

  function formatKrMd(amount) {
    return amount + ' kr/md';
  }

  function formatKrSlashMd(amount) {
    return amount + ' kr./md';
  }

  function getUrgencyDurationMs(campaign, cfg) {
    if (campaign && campaign.urgencyMinutes != null) return campaign.urgencyMinutes * 60 * 1000;
    if (cfg && cfg.urgencyMinutes != null) return cfg.urgencyMinutes * 60 * 1000;
    if (campaign && campaign.urgencyHours != null) return campaign.urgencyHours * 60 * 60 * 1000;
    if (cfg && cfg.urgencyHours != null) return cfg.urgencyHours * 60 * 60 * 1000;
    return 30 * 60 * 1000;
  }

  function getExclusiveLabel(campaign, cfg) {
    if (campaign && campaign.exclusiveLabel) return campaign.exclusiveLabel;
    if (cfg && cfg.exclusiveLabel) return cfg.exclusiveLabel;
    return 'Din personlige rabat · kun for dig';
  }

  function getUrgencyLabel(campaign, cfg) {
    if (campaign && campaign.urgencyLabel) return campaign.urgencyLabel;
    if (cfg && cfg.urgencyLabel) return cfg.urgencyLabel;
    return 'Udløber om';
  }

  function formatCampaignBadge(campaign) {
    if (campaign.badge) return campaign.badge;
    if (campaign.type === 'percent') return campaign.amount + '% rabat';
    return '−' + campaign.amount + ' kr';
  }

  function getUrgencyEndAt(campaignKey, campaign, cfg) {
    var stored = loadStored();
    var durationMs = getUrgencyDurationMs(campaign, cfg);

    if (stored && stored.key === campaignKey && stored.urgencyEndAt) {
      return stored.urgencyEndAt;
    }

    var startAt = stored && stored.savedAt ? stored.savedAt : Date.now();
    var endAt = startAt + durationMs;

    if (stored && stored.key === campaignKey) {
      try {
        stored.urgencyEndAt = endAt;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } catch (e) {
        /* ignore */
      }
    }

    return endAt;
  }

  function formatCountdown(ms) {
    if (ms <= 0) return '00:00:00';
    var totalSeconds = Math.floor(ms / 1000);
    var h = Math.floor(totalSeconds / 3600);
    var m = Math.floor((totalSeconds % 3600) / 60);
    var s = totalSeconds % 60;
    return (
      String(h).padStart(2, '0') +
      ':' +
      String(m).padStart(2, '0') +
      ':' +
      String(s).padStart(2, '0')
    );
  }

  function isUrgencyDismissed(campaignKey) {
    try {
      return sessionStorage.getItem(URGENCY_DISMISS_KEY) === campaignKey;
    } catch (e) {
      return false;
    }
  }

  function dismissUrgencyBanner(campaignKey) {
    try {
      sessionStorage.setItem(URGENCY_DISMISS_KEY, campaignKey);
    } catch (e) {
      /* ignore */
    }
    hideUrgencyBanner();
  }

  function hideUrgencyBanner() {
    if (urgencyTimerId) {
      clearInterval(urgencyTimerId);
      urgencyTimerId = null;
    }
    var banner = document.getElementById('sc-campaign-urgency');
    if (banner) {
      banner.classList.remove('sc-campaign-urgency--visible');
      banner.classList.add('sc-campaign-urgency--hidden');
      window.setTimeout(function () {
        if (banner.parentNode) banner.parentNode.removeChild(banner);
      }, 420);
    }
    document.documentElement.classList.remove('sc-campaign-urgency-visible');
    document.documentElement.style.removeProperty('--sc-campaign-banner-h');
  }

  function setBannerOffset(banner) {
    if (!banner) return;
    document.documentElement.style.setProperty('--sc-campaign-banner-h', banner.offsetHeight + 'px');
    document.documentElement.classList.add('sc-campaign-urgency-visible');
  }

  function loadStored() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function saveStored(campaignKey, campaign) {
    var cfg = parseConfig();
    var durationMs = getUrgencyDurationMs(campaign, cfg);
    var now = Date.now();
    var existing = loadStored();
    var savedAt =
      existing && existing.key === campaignKey && existing.savedAt ? existing.savedAt : now;
    var urgencyEndAt =
      existing && existing.key === campaignKey && existing.urgencyEndAt
        ? existing.urgencyEndAt
        : savedAt + durationMs;

    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          key: campaignKey,
          code: campaign.code,
          type: campaign.type,
          amount: campaign.amount,
          label: campaign.label || cfg.labelDefault || 'Din kampagnepris',
          badge: campaign.badge || '',
          savedAt: savedAt,
          urgencyEndAt: urgencyEndAt
        })
      );
    } catch (e) {
      /* ignore quota errors */
    }
  }

  function resolveCampaignKeyFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var discountParam = params.get('discount') || params.get('promo') || params.get('code');
    if (discountParam) {
      return discountParam.trim().toUpperCase();
    }
    var utmCampaign = params.get('utm_campaign');
    if (utmCampaign) {
      var cfg = parseConfig();
      var mapped = cfg.utmMap[utmCampaign.toLowerCase()] || cfg.utmMap[utmCampaign];
      if (mapped) return mapped.toUpperCase();
      return utmCampaign.trim().toUpperCase();
    }
    return null;
  }

  function getCampaign(key) {
    if (!key) return null;
    var cfg = parseConfig();
    return cfg.campaigns[key] || cfg.campaigns[key.toUpperCase()] || null;
  }

  function campaignAppliesToTier(campaign, tierAmount) {
    if (!campaign.tiers || !campaign.tiers.length) return true;
    return campaign.tiers.indexOf(tierAmount) !== -1;
  }

  function computeDiscountedPrice(originalAmount, campaign) {
    if (!campaign || originalAmount == null) return null;
    if (campaign.type === 'percent') {
      return Math.max(0, Math.round(originalAmount * (1 - campaign.amount / 100)));
    }
    return Math.max(0, originalAmount - campaign.amount);
  }

  function resolveActiveCampaign() {
    if (ACTIVE) return ACTIVE;

    var cfg = parseConfig();
    var urlKey = resolveCampaignKeyFromUrl();
    var campaign = null;
    var campaignKey = null;

    if (urlKey) {
      campaign = getCampaign(urlKey);
      if (campaign) {
        campaignKey = urlKey.toUpperCase();
        saveStored(campaignKey, campaign);
      }
    }

    if (!campaign) {
      var stored = loadStored();
      if (stored && stored.key) {
        campaign = getCampaign(stored.key);
        if (campaign) {
          campaignKey = stored.key;
          ACTIVE = {
            key: campaignKey,
            code: campaign.code || stored.code,
            type: campaign.type || stored.type,
            amount: campaign.amount != null ? campaign.amount : stored.amount,
            label: campaign.label || stored.label || cfg.labelDefault,
            badge: campaign.badge || stored.badge || '',
            tiers: campaign.tiers || null
          };
          return ACTIVE;
        }
      }
      ACTIVE = null;
      return null;
    }

    ACTIVE = {
      key: campaignKey,
      code: campaign.code,
      type: campaign.type,
      amount: campaign.amount,
      label: campaign.label || cfg.labelDefault,
      badge: campaign.badge || '',
      tiers: campaign.tiers || null
    };
    return ACTIVE;
  }

  function injectStyles() {
    if (document.getElementById('sc-campaign-discount-styles')) return;
    var style = document.createElement('style');
    style.id = 'sc-campaign-discount-styles';
    style.textContent = [
      '.sc-campaign-badge{display:inline-flex;align-items:center;gap:4px;background:rgba(197,168,122,0.16);color:#8C6545;font-family:var(--font-sans);font-size:10px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;padding:4px 10px;border-radius:999px;margin:0 0 8px;}',
      '.sc-campaign-price-row{display:flex;flex-wrap:wrap;align-items:baseline;gap:6px 8px;}',
      '.sc-campaign-price-row .sc-tier__price,.sc-campaign-price-row .sc-tier__price--discounted{font-family:var(--font-display);font-size:24px;font-weight:400;letter-spacing:-0.02em;color:#1C1814;line-height:1;}',
      '.sc-campaign-price-row .sc-tier__price--discounted{color:#8C6545;}',
      '.sc-campaign-price-row .sc-tier__price--original{font-family:var(--font-sans);font-size:13px;color:rgba(28,24,20,0.42);text-decoration:line-through;}',
      '.sc-campaign-price-row .sc-tier__period{font-family:var(--font-sans);font-size:13px;color:rgba(28,24,20,0.5);}',
      '@media(min-width:901px){.sc-campaign-price-row .sc-tier__price,.sc-campaign-price-row .sc-tier__price--discounted{font-size:46px;}.sc-campaign-price-row .sc-tier__price--original{font-size:15px;}}',
      '.sc-mship-bar__price--campaign{display:flex;flex-direction:column;align-items:flex-start;gap:2px;}',
      '.sc-mship-bar__price-original{font-family:var(--font-sans);font-size:11px;color:rgba(28,24,20,0.42);text-decoration:line-through;line-height:1;}',
      '.sc-mship-bar__price-discounted{font-family:var(--font-display);font-size:18px;color:var(--sc-gold);line-height:1.1;}',
      '.sc-mship-bar__campaign-label{font-family:var(--font-sans);font-size:9px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:#8C6545;}',
      '.sc-tiers__from--campaign strong{color:#8C6545;}',
      '.sc-tiers__from-strike{text-decoration:line-through;color:rgba(28,24,20,0.42);font-weight:400;margin-right:4px;}',
      '.sc-tiers__campaign-banner{display:flex;justify-content:center;margin:0 auto 12px;max-width:520px;}',
      '.sc-tier__strike--hidden{display:none!important;}',
      '.sc-campaign-price--active{display:flex;flex-direction:column;align-items:flex-start;gap:6px;}',
      '.sc-hero__price.sc-campaign-price--active,.sc-finalcta__price.sc-campaign-price--active{align-items:center;}',
      '.sc-hero__price-row .sc-hero__price-amount.sc-tier__price--discounted,.sc-finalcta__price-row .sc-finalcta__price-amount.sc-tier__price--discounted{font-family:var(--font-display);color:#8C6545;}',
      '.sc-hero__price-row .sc-hero__price-amount.sc-tier__price--original,.sc-finalcta__price-row .sc-finalcta__price-amount.sc-tier__price--original{font-size:18px;}',
      '.sc-why__price-amount.sc-campaign-price--active,.sc-why__card-price-value.sc-campaign-price--active{color:#8C6545;}',
      '.sc-why__card-sub.sc-campaign-price--active{color:#8C6545;}',
      '.sc-sticky__title.sc-campaign-price--active{color:#1C1814;}',
      '.sc-campaign-global-banner{display:flex;justify-content:center;align-items:center;padding:8px 16px;background:rgba(197,168,122,0.14);border-bottom:1px solid rgba(197,168,122,0.22);}',
      '.sc-campaign-global-banner .sc-campaign-badge{margin:0;}',
      '.sc-hero__price .sc-campaign-price-prefix{font-family:var(--font-sans);font-size:13px;color:var(--sc-text-muted);}',
      '.sc-hero__price .sc-campaign-price-row .sc-tier__price--discounted{font-family:var(--font-display);font-size:32px;font-weight:500;color:#8C6545;letter-spacing:-0.02em;}',
      '.sc-hero__price .sc-campaign-price-row .sc-tier__price--original{font-size:18px;}',
      '.sc-hero__price .sc-campaign-price-row .sc-tier__period{font-family:var(--font-sans);font-size:13px;color:var(--sc-text-muted);}',
      '.sc-finalcta__price .sc-campaign-price-prefix{font-family:var(--font-sans);font-size:13px;color:var(--sc-text-muted);}',
      '.sc-finalcta__price .sc-campaign-price-row .sc-tier__price--discounted{font-family:var(--font-display);font-size:26px;font-weight:500;color:var(--sc-gold-dark);letter-spacing:-0.02em;}',
      '.sc-finalcta__price .sc-campaign-price-row .sc-tier__price--original{font-size:16px;}',
      '.sc-finalcta__price .sc-campaign-price-row .sc-tier__period{font-family:var(--font-sans);font-size:13px;color:var(--sc-text-muted);}',
      '.sc-campaign-urgency{position:fixed;top:0;left:0;right:0;z-index:10050;padding:10px 12px;padding-top:calc(10px + env(safe-area-inset-top,0px));background:#FAF8F4;border-bottom:1px solid rgba(197,168,122,0.35);box-shadow:0 4px 24px rgba(28,24,20,0.08);transform:translateY(-110%);opacity:0;transition:transform 480ms cubic-bezier(0.22,1,0.36,1),opacity 360ms ease;pointer-events:auto;}',
      '.sc-campaign-urgency--visible{transform:translateY(0);opacity:1;}',
      '.sc-campaign-urgency--hidden{transform:translateY(-110%);opacity:0;}',
      '.sc-campaign-urgency__inner{display:flex;align-items:center;gap:10px 12px;max-width:1200px;margin:0 auto;}',
      '.sc-campaign-urgency__badge{flex-shrink:0;position:relative;overflow:hidden;font-family:var(--font-sans);font-size:10px;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;color:#8C6545;background:rgba(197,168,122,0.2);padding:5px 11px;border-radius:999px;animation:sc-campaign-badge-pulse 2.8s ease-in-out infinite;}',
      '.sc-campaign-urgency__badge::after{content:"";position:absolute;inset:0;background:linear-gradient(105deg,transparent 35%,rgba(255,255,255,0.55) 50%,transparent 65%);transform:translateX(-120%);animation:sc-campaign-badge-shimmer 3.6s ease-in-out infinite;}',
      '@keyframes sc-campaign-badge-pulse{0%,100%{transform:scale(1);}50%{transform:scale(1.03);}}',
      '@keyframes sc-campaign-badge-shimmer{0%,72%,100%{transform:translateX(-120%);}28%{transform:translateX(120%);}}',
      '.sc-campaign-urgency__text{flex:1;min-width:0;display:flex;flex-direction:column;gap:1px;}',
      '.sc-campaign-urgency__exclusive{font-family:var(--font-display);font-size:15px;font-weight:500;color:#1C1814;line-height:1.25;}',
      '.sc-campaign-urgency__timer{font-family:var(--font-sans);font-size:11px;font-weight:500;letter-spacing:0.03em;color:#8C6545;}',
      '.sc-campaign-urgency__close{flex-shrink:0;display:flex;align-items:center;justify-content:center;width:28px;height:28px;border:none;background:transparent;color:rgba(28,24,20,0.45);cursor:pointer;border-radius:999px;transition:background 180ms ease,color 180ms ease;}',
      '.sc-campaign-urgency__close:hover{background:rgba(197,168,122,0.16);color:#1C1814;}',
      'html.sc-campaign-urgency-visible .shopify-section-group-header-group,html.sc-campaign-urgency-visible .section-header,html.sc-campaign-urgency-visible sticky-header{top:var(--sc-campaign-banner-h,0px)!important;}',
      '@media(max-width:749px){.sc-campaign-urgency__exclusive{font-size:14px;}.sc-campaign-urgency__inner{align-items:flex-start;}}',
      '@media(min-width:750px){.sc-campaign-urgency__exclusive{font-size:16px;}.sc-campaign-urgency__inner{gap:14px;}}'
    ].join('');
    document.head.appendChild(style);
  }

  function buildPriceRowHtml(originalAmount, discountedAmount, period, showLabel) {
    var periodText = period || '/md';
    var html =
      (showLabel ? '<div class="sc-campaign-badge">' + escapeHtml(ACTIVE.label) + '</div>' : '') +
      '<div class="sc-campaign-price-row">' +
      '<span class="sc-tier__price--original">' + escapeHtml(formatKr(originalAmount)) + '</span>' +
      '<span class="sc-tier__price sc-tier__price--discounted">' + escapeHtml(formatKr(discountedAmount)) + '</span>' +
      '<span class="sc-tier__period">' + escapeHtml(periodText) + '</span>' +
      '</div>';
    return html;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function updateTierCards() {
    if (!ACTIVE) return;
    document.querySelectorAll('.sc-tier--selectable[data-tier-price-amount]').forEach(function (card) {
      var amount = parseInt(card.getAttribute('data-tier-price-amount'), 10);
      if (!amount || !campaignAppliesToTier(ACTIVE, amount)) return;

      var discounted = computeDiscountedPrice(amount, ACTIVE);
      if (discounted == null || discounted >= amount) return;

      var period = card.getAttribute('data-tier-period') || '/md';
      var priceRow = card.querySelector('.sc-tier__price-row');
      if (!priceRow || priceRow.dataset.scCampaignApplied === 'true') return;

      priceRow.dataset.scCampaignOriginal = priceRow.innerHTML;
      priceRow.dataset.scCampaignApplied = 'true';
      priceRow.innerHTML = buildPriceRowHtml(amount, discounted, period, true);

      var strike = card.querySelector('.sc-tier__strike');
      if (strike) strike.classList.add('sc-tier__strike--hidden');

      card.setAttribute('data-tier-price-discounted', String(discounted));
      card.setAttribute('data-tier-price', formatKr(discounted) + period);
    });
  }

  function updateFromPrice() {
    if (!ACTIVE) return;
    var fromEl = document.querySelector('.sc-tiers__from');
    if (!fromEl) return;

    var amounts = [];
    document.querySelectorAll('.sc-tier--selectable[data-tier-price-amount]').forEach(function (card) {
      var amount = parseInt(card.getAttribute('data-tier-price-amount'), 10);
      if (!amount || !campaignAppliesToTier(ACTIVE, amount)) return;
      var discounted = computeDiscountedPrice(amount, ACTIVE);
      if (discounted != null && discounted < amount) amounts.push({ original: amount, discounted: discounted });
    });
    if (!amounts.length) return;

    amounts.sort(function (a, b) {
      return a.discounted - b.discounted;
    });
    var lowest = amounts[0];
    fromEl.innerHTML =
      'Fra <span class="sc-tiers__from-strike">' +
      escapeHtml(formatKr(lowest.original)) +
      '</span><strong class="sc-tiers__from--campaign">' +
      escapeHtml(formatKr(lowest.discounted)) +
      '</strong>/md';
  }

  function showCampaignBanner() {
    if (!ACTIVE || !ACTIVE.badge) return;
    var tiers = document.getElementById('sc-pricing');
    if (!tiers || tiers.querySelector('.sc-tiers__campaign-banner')) return;
    var banner = document.createElement('div');
    banner.className = 'sc-tiers__campaign-banner';
    banner.innerHTML = '<span class="sc-campaign-badge">' + escapeHtml(ACTIVE.badge) + '</span>';
    var head = tiers.querySelector('.sc-tiers__head');
    if (head && head.nextElementSibling) {
      head.parentNode.insertBefore(banner, head.nextElementSibling);
    } else if (head) {
      head.appendChild(banner);
    }
  }

  function formatMobileBarPrice(originalAmount, discountedAmount) {
    return (
      '<span class="sc-mship-bar__campaign-label">' +
      escapeHtml(ACTIVE.label) +
      '</span>' +
      '<span class="sc-mship-bar__price-original">' +
      escapeHtml(formatKrSlashMd(originalAmount)) +
      '</span>' +
      '<span class="sc-mship-bar__price-discounted">' +
      escapeHtml(formatKrSlashMd(discountedAmount)) +
      '</span>'
    );
  }

  function getDiscountedDisplayForAmount(amount, suffix) {
    if (!ACTIVE || amount == null) return null;
    if (!campaignAppliesToTier(ACTIVE, amount)) return null;
    var discounted = computeDiscountedPrice(amount, ACTIVE);
    if (discounted == null || discounted >= amount) return null;
    suffix = suffix || '/md';
    return {
      original: amount,
      discounted: discounted,
      text: formatKr(discounted) + suffix,
      textMd: formatKrMd(discounted),
      textSlashMd: formatKrSlashMd(discounted),
      html: formatMobileBarPrice(amount, discounted)
    };
  }

  function updateMobileBarFromCard(card) {
    if (!ACTIVE || !card) return;
    var amount = parseInt(card.getAttribute('data-tier-price-amount'), 10);
    var barPriceEl = document.getElementById('sc-mship-bar-price');
    if (!barPriceEl || !amount) return;
    var display = getDiscountedDisplayForAmount(amount, '/md');
    if (!display) return;
    barPriceEl.classList.add('sc-mship-bar__price--campaign');
    barPriceEl.innerHTML = display.html;
  }

  function updateScentPlanMeta() {
    if (!ACTIVE) return;
    var node = document.getElementById('sc-scent-plan');
    if (!node) return;
    try {
      var data = JSON.parse(node.textContent || '{}');
      var amount = parsePriceAmount(data.priceAmount || data.priceLabel);
      if (!amount) return;
      var display = getDiscountedDisplayForAmount(amount, '/md');
      if (!display) return;
      data.priceLabelOriginal = data.priceLabel;
      data.priceLabel = display.textMd;
      data.priceAmount = amount;
      data.priceDiscounted = display.discounted;
      node.textContent = JSON.stringify(data);
    } catch (e) {
      /* ignore */
    }
  }

  function refreshBarIfVisible() {
    if (!ACTIVE) return;
    var selected = document.querySelector('.sc-tier--selected[data-tier-price-amount]');
    if (selected) updateMobileBarFromCard(selected);
  }

  function buildFromRowHtml(prefix, originalAmount, discountedAmount, suffix, showBadge) {
    return (
      (showBadge ? '<div class="sc-campaign-badge">' + escapeHtml(ACTIVE.label) + '</div>' : '') +
      '<div class="sc-campaign-price-row">' +
      (prefix ? '<span class="sc-campaign-price-prefix">' + escapeHtml(prefix) + '</span>' : '') +
      '<span class="sc-tier__price--original">' + escapeHtml(formatKr(originalAmount)) + '</span>' +
      '<span class="sc-tier__price sc-tier__price--discounted">' + escapeHtml(formatKr(discountedAmount)) + '</span>' +
      (suffix ? '<span class="sc-tier__period">' + escapeHtml(suffix) + '</span>' : '') +
      '</div>'
    );
  }

  function buildInlineStrikeHtml(prefix, originalAmount, discountedAmount, suffix) {
    return (
      (prefix || '') +
      '<span class="sc-tiers__from-strike">' +
      escapeHtml(formatKr(originalAmount)) +
      '</span>' +
      '<strong class="sc-tiers__from--campaign">' +
      escapeHtml(formatKr(discountedAmount)) +
      '</strong>' +
      (suffix || '')
    );
  }

  function buildFromTextHtml(originalAmount, discountedAmount, suffix) {
    return (
      'Fra <span class="sc-tiers__from-strike">' +
      escapeHtml(formatKrMd(originalAmount)) +
      '</span><strong class="sc-tiers__from--campaign">' +
      escapeHtml(formatKrMd(discountedAmount)) +
      '</strong>' +
      (suffix || '')
    );
  }

  function updateCampaignPriceElements() {
    if (!ACTIVE) return;

    document.querySelectorAll('[data-sc-campaign-price]').forEach(function (el) {
      if (el.dataset.scCampaignApplied === 'true') return;

      var amount = parseInt(el.getAttribute('data-sc-campaign-price'), 10);
      if (!amount) return;

      var suffix = el.getAttribute('data-sc-campaign-suffix') || '/md';
      var display = getDiscountedDisplayForAmount(amount, suffix);
      if (!display) return;

      var mode = el.getAttribute('data-sc-campaign-price-mode') || 'inline-strike';
      var prefix = el.getAttribute('data-sc-campaign-prefix') || '';
      var showBadge = el.getAttribute('data-sc-campaign-badge') === 'true';
      var html = '';

      if (mode === 'from-row') {
        html = buildFromRowHtml(
          el.getAttribute('data-sc-campaign-label') || 'Fra kun',
          amount,
          display.discounted,
          suffix,
          showBadge
        );
      } else if (mode === 'from-text') {
        html = buildFromTextHtml(amount, display.discounted, el.getAttribute('data-sc-campaign-suffix') || '');
      } else {
        html = buildInlineStrikeHtml(prefix, amount, display.discounted, suffix);
      }

      el.dataset.scCampaignOriginal = el.innerHTML;
      el.dataset.scCampaignApplied = 'true';
      el.classList.add('sc-campaign-price--active');
      el.innerHTML = html;
    });
  }

  function updateCtaPriceElements() {
    if (!ACTIVE) return;

    document.querySelectorAll('[data-sc-campaign-cta-price]').forEach(function (el) {
      if (el.dataset.scCampaignApplied === 'true') return;

      var amount = parseInt(el.getAttribute('data-sc-campaign-cta-price'), 10);
      if (!amount) return;

      var display = getDiscountedDisplayForAmount(amount, '/md');
      if (!display) return;

      var template = el.getAttribute('data-sc-campaign-cta-template');
      var nextText = template
        ? template.replace(/\{price\}/g, formatKr(display.discounted))
        : el.textContent.replace(/\d+\s*kr\.?/i, formatKr(display.discounted));

      el.dataset.scCampaignOriginal = el.textContent;
      el.dataset.scCampaignApplied = 'true';
      el.textContent = nextText;
    });
  }

  function ensureTiersFromPrice() {
    if (!ACTIVE) return;

    var fromEl = document.querySelector('.sc-tiers__from');
    if (fromEl) return;

    var tiers = document.getElementById('sc-pricing');
    if (!tiers) return;

    var amounts = [];
    tiers.querySelectorAll('.sc-tier--selectable[data-tier-price-amount]').forEach(function (card) {
      var amount = parseInt(card.getAttribute('data-tier-price-amount'), 10);
      if (!amount || !campaignAppliesToTier(ACTIVE, amount)) return;
      var discounted = computeDiscountedPrice(amount, ACTIVE);
      if (discounted != null && discounted < amount) {
        amounts.push({ original: amount, discounted: discounted });
      }
    });
    if (!amounts.length) return;

    amounts.sort(function (a, b) {
      return a.discounted - b.discounted;
    });

    fromEl = document.createElement('p');
    fromEl.className = 'sc-tiers__from';
    fromEl.innerHTML =
      'Fra <strong>' + escapeHtml(formatKr(amounts[0].original)) + '</strong>/md';

    var heading = tiers.querySelector('.sc-tiers__heading');
    if (heading && heading.parentNode) {
      heading.parentNode.insertBefore(fromEl, heading.nextSibling);
    }
  }

  function showUrgencyBanner() {
    if (!ACTIVE) return;
    if (isUrgencyDismissed(ACTIVE.key)) return;
    if (document.getElementById('sc-campaign-urgency')) return;

    injectStyles();

    var cfg = parseConfig();
    var campaign = getCampaign(ACTIVE.key) || ACTIVE;
    var exclusiveLabel = getExclusiveLabel(campaign, cfg);
    var urgencyLabel = getUrgencyLabel(campaign, cfg);
    var badgeText = formatCampaignBadge(campaign);
    var endAt = getUrgencyEndAt(ACTIVE.key, campaign, cfg);

    var banner = document.createElement('div');
    banner.id = 'sc-campaign-urgency';
    banner.className = 'sc-campaign-urgency';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    banner.innerHTML =
      '<div class="sc-campaign-urgency__inner">' +
      '<span class="sc-campaign-urgency__badge">' +
      escapeHtml(badgeText) +
      '</span>' +
      '<div class="sc-campaign-urgency__text">' +
      '<span class="sc-campaign-urgency__exclusive">' +
      escapeHtml(exclusiveLabel) +
      '</span>' +
      '<span class="sc-campaign-urgency__timer"></span>' +
      '</div>' +
      '<button type="button" class="sc-campaign-urgency__close" aria-label="Luk">' +
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
      '<path d="M3 3l8 8M11 3L3 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>' +
      '</button>' +
      '</div>';

    document.body.appendChild(banner);

    banner.querySelector('.sc-campaign-urgency__close').addEventListener('click', function () {
      dismissUrgencyBanner(ACTIVE.key);
    });

    var timerEl = banner.querySelector('.sc-campaign-urgency__timer');

    function tick() {
      var remaining = endAt - Date.now();
      if (remaining <= 0) {
        hideUrgencyBanner();
        return;
      }
      timerEl.textContent = urgencyLabel + ' ' + formatCountdown(remaining);
    }

    tick();
    urgencyTimerId = window.setInterval(tick, 1000);

    window.requestAnimationFrame(function () {
      setBannerOffset(banner);
      banner.classList.add('sc-campaign-urgency--visible');
    });
  }

  function applyPriceDisplays() {
    if (!ACTIVE) return;
    injectStyles();
    ensureTiersFromPrice();
    showCampaignBanner();
    showUrgencyBanner();
    updateTierCards();
    updateFromPrice();
    updateCampaignPriceElements();
    updateCtaPriceElements();
    refreshBarIfVisible();
    updateScentPlanMeta();
    document.documentElement.classList.add('sc-campaign-active');
    document.dispatchEvent(
      new CustomEvent('scenty:campaign:applied', { detail: { campaign: ACTIVE } })
    );
  }

  function getCheckoutUrl(targetPath) {
    var path = targetPath || '/checkout';
    if (!ACTIVE || !ACTIVE.code) return path;
    return '/discount/' + encodeURIComponent(ACTIVE.code) + '?redirect=' + encodeURIComponent(path);
  }

  function getActive() {
    return resolveActiveCampaign();
  }

  function clearCampaign() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(URGENCY_DISMISS_KEY);
    } catch (e) {
      /* ignore */
    }
    hideUrgencyBanner();
    ACTIVE = null;
    document.documentElement.classList.remove('sc-campaign-active');
  }

  function init() {
    ensureDiscountCookie(function () {
      resolveActiveCampaign();
      if (ACTIVE) applyPriceDisplays();
    });

    document.addEventListener('scenty:tier:selected', function (e) {
      if (e.detail && e.detail.card) updateMobileBarFromCard(e.detail.card);
    });
  }

  function ensureDiscountCookie(done) {
    var campaign = resolveActiveCampaign();
    if (!campaign || !campaign.code) {
      done();
      return;
    }

    try {
      if (sessionStorage.getItem('sc_campaign_cookie_set') === campaign.code) {
        done();
        return;
      }
    } catch (e) {
      /* ignore */
    }

    var params = new URLSearchParams(window.location.search);
    var hasUrlTrigger =
      params.has('discount') ||
      params.has('promo') ||
      params.has('code') ||
      (params.has('utm_campaign') && !!resolveCampaignKeyFromUrl());

    if (!hasUrlTrigger) {
      done();
      return;
    }

    try {
      sessionStorage.setItem('sc_campaign_cookie_set', campaign.code);
    } catch (e) {
      /* ignore */
    }

    var cleanParams = new URLSearchParams(window.location.search);
    cleanParams.delete('discount');
    cleanParams.delete('promo');
    cleanParams.delete('code');

    var redirectPath = window.location.pathname;
    var qs = cleanParams.toString();
    if (qs) redirectPath += '?' + qs;

    window.location.replace(
      '/discount/' +
        encodeURIComponent(campaign.code) +
        '?redirect=' +
        encodeURIComponent(redirectPath)
    );
  }

  window.ScentyCampaignDiscount = {
    init: init,
    getActive: getActive,
    getCheckoutUrl: getCheckoutUrl,
    getDiscountedDisplayForAmount: getDiscountedDisplayForAmount,
    computeDiscountedPrice: function (amount) {
      var campaign = resolveActiveCampaign();
      if (!campaign) return null;
      return computeDiscountedPrice(amount, campaign);
    },
    refresh: applyPriceDisplays,
    clear: clearCampaign,
    parsePriceAmount: parsePriceAmount
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
